import React from 'react';
import {
  Add as AddIcon,
  DeleteOutlined as DeleteOutlineIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { Box, Button, CircularProgress } from '@mui/material';
import { guaranteesClientPageStyles } from '~/modules/guarantees/form/client/styles.ts';
import FlowSection from '../../components/root/shared/FlowSection.tsx';
import ButtonLoadingIcon from '../../../shared/ButtonLoadingIcon.tsx';
import { UDBTF_GUARANTEE_SCAN_COPY_TEXT } from './constants.ts';

type TUdbtfGuaranteeScanCopySectionProps = {
  isDeleting: boolean;
  isDisabled: boolean;
  isPreviewLoading: boolean;
  isUploading: boolean;
  previewUrl: string;
  onAddDocument: (file?: File | null) => void;
  onDeleteDocument: () => void;
  onOpenDocument: () => void;
};

const UdbtfGuaranteeScanCopySection: React.FC<TUdbtfGuaranteeScanCopySectionProps> = ({
  isDeleting,
  isDisabled,
  isPreviewLoading,
  isUploading,
  previewUrl,
  onAddDocument,
  onDeleteDocument,
  onOpenDocument,
}) => (
  <FlowSection
    step={UDBTF_GUARANTEE_SCAN_COPY_TEXT.sectionStep}
    title={UDBTF_GUARANTEE_SCAN_COPY_TEXT.sectionTitle}
    description={UDBTF_GUARANTEE_SCAN_COPY_TEXT.sectionDescription}
  >
    <Box sx={guaranteesClientPageStyles.generatedDocumentPreview}>
      {isPreviewLoading ? (
        <Box sx={guaranteesClientPageStyles.flowLoadingState}>
          <CircularProgress size={24} />
          {UDBTF_GUARANTEE_SCAN_COPY_TEXT.previewLoadingText}
        </Box>
      ) : previewUrl ? (
        <Box
          component="iframe"
          src={previewUrl}
          title={UDBTF_GUARANTEE_SCAN_COPY_TEXT.previewTitle}
          sx={guaranteesClientPageStyles.generatedDocumentIframe}
        />
      ) : (
        <Box sx={guaranteesClientPageStyles.flowLoadingState}>
          {UDBTF_GUARANTEE_SCAN_COPY_TEXT.emptyPreviewText}
        </Box>
      )}

      <Box sx={guaranteesClientPageStyles.generatedDocumentActions}>
        {!previewUrl ? (
          <Button
            component="label"
            variant="outlined"
            color="primary"
            disabled={isDisabled || isUploading}
            startIcon={isUploading ? <ButtonLoadingIcon /> : <AddIcon />}
            sx={guaranteesClientPageStyles.generatedDocumentOpenButton}
          >
            {UDBTF_GUARANTEE_SCAN_COPY_TEXT.addDocumentButton}
            <input
              hidden
              type="file"
              onChange={(event) => {
                onAddDocument(event.target.files?.[0]);
                event.target.value = '';
              }}
            />
          </Button>
        ) : (
          <>
            <Button
              variant="outlined"
              color="primary"
              type="button"
              disabled={isDisabled || isPreviewLoading}
              startIcon={<OpenInNewIcon />}
              sx={guaranteesClientPageStyles.generatedDocumentOpenButton}
              onClick={onOpenDocument}
            >
              {UDBTF_GUARANTEE_SCAN_COPY_TEXT.openButton}
            </Button>
            <Button
              variant="outlined"
              color="error"
              type="button"
              disabled={isDisabled || isDeleting}
              startIcon={isDeleting ? <ButtonLoadingIcon /> : <DeleteOutlineIcon />}
              sx={guaranteesClientPageStyles.generatedDocumentOpenButton}
              onClick={onDeleteDocument}
            >
              {UDBTF_GUARANTEE_SCAN_COPY_TEXT.deleteButton}
            </Button>
          </>
        )}
      </Box>
    </Box>
  </FlowSection>
);

export default UdbtfGuaranteeScanCopySection;
